import {
  CONTACT_INFO,
  formatDate,
  formatLocation,
  hasInfoField,
  processSenasParticulares,
  processVestimenta,
  verifyAge,
  verifyStature,
} from "Utils/MissingPersonUtils";
import { Page, Font, Text, View, Image, Document, Svg, Path } from "@react-pdf/renderer";
import MontserratRegular from "Assets/fonts/Montserrat-Regular.ttf";
import MontserratBold from "Assets/fonts/Montserrat-Bold.ttf";
import MontserratExtraBold from "Assets/fonts/Montserrat-ExtraBold.ttf";
import MontserratBlack from "Assets/fonts/Montserrat-Black.ttf";
import { LOGO_COBUPEJ_CEDULA_PNG, LOGO_JAL_CEDULA_PNG } from "Assets/index.js";
import { PDF as S, PDF_PAGE, PNG_IMP, PNG_IMP_L, titularFor } from "./styles";
import { DISCLAIMER } from "./pngCapture";

// La misma familia y pesos que en src/index.css.
Font.register({
  family: "Montserrat Cedula",
  fonts: [
    { src: MontserratRegular, fontWeight: 400 },
    { src: MontserratBold, fontWeight: 700 },
    { src: MontserratExtraBold, fontWeight: 800 },
    { src: MontserratBlack, fontWeight: 900 },
  ],
});
// Como la impresion: se parte por palabras, sin guiones (react-pdf silabea en ingles por defecto).
Font.registerHyphenationCallback((word) => [word]);

// Los iconos de la impresion (assets/icons/logo-fb.svg y logo-x.svg): circulo blanco y glifo azul.
// Van como Svg porque react-pdf no pinta SVG en <Image>.
const ICON_VIEWBOX = "0 0 1080 1073.8";
const ICON_CIRCLE = "M1080,536.9c0,296.5-241.8,536.9-540,536.9S0,833.4,0,536.9,241.8,0,540,0s540,240.4,540,536.9";
const FACEBOOK_PATH = "M599.7,878.8v-299.2h100.3l19.1-123.8h-119.4v-80.3c0-33.9,16.7-66.9,70.2-66.9h54.3v-105.4s-49.3-8.4-96.4-8.4c-98.4,0-162.7,59.3-162.7,166.6v94.4h-109.4v123.7h109.4v299.2h134.6Z";
const X_PATH = "M527.6,566.6l-19-27-151.4-215.4h65.2l122.2,173.8,19,27,158.9,225.9h-65.2l-129.6-184.3h0s0,0,0,0ZM585.8,499.5l179.1-207h-42.4l-155.5,179.7-124.2-179.7h-143.3l187.8,271.7-187.8,217h42.4l164.2-189.8,131.2,189.8h143.2l-194.8-281.8h0Z";

const RedesIcon = ({ d }) => (
  <Svg viewBox={ICON_VIEWBOX} style={S.redesIcon}>
    <Path d={ICON_CIRCLE} fill="#FFFFFF" />
    <Path d={d} fill="#02ADD8" />
  </Svg>
);

const Row = ({ label, value }) => (
  <Text style={S.dataRow}>
    <Text style={S.dataLabel}>{label}: </Text>
    <Text style={S.dataValue}>{value}</Text>
  </Text>
);

// La impresion deja desbordar señas y vestimenta; en el PDF se cortan en linea entera con "…".
const boxLines = (space) => ({ maxLines: Math.floor(space / parseFloat(PNG_IMP.boxText.lineHeight)), textOverflow: "ellipsis" });

const MissingPersonPdf = ({ missingPersonData, photoSrc = missingPersonData.ruta_foto, qrCodeString }) => {
  const {
    condicion_localizacion,
    colonia,
    municipio,
    estado,
    nombre_completo,
    edad_momento_desaparicion,
    genero,
    sexo,
    complexion,
    estatura,
    tez,
    cabello,
    ojos_color,
    fecha_desaparicion,
    descripcion_sena_particular,
    descripcion_vestimenta,
  } = missingPersonData;

  const titular = titularFor(condicion_localizacion);
  const vestimenta = processVestimenta(descripcion_vestimenta);
  const senas = processSenasParticulares(descripcion_sena_particular);
  const ubicacion = formatLocation(colonia, municipio, estado);
  // Iguala el espaciado de HTML para que los saltos de línea no agoten maxLines en el PDF.
  const vestimentaText = (vestimenta.length ? vestimenta.map((i) => i.formattedText).join(". ") : "SIN DATOS").replace(/\s+/g, " ");
  const senasText = (senas.length ? senas.map((i) => i.formattedText).join(". ") : "SIN DATOS").replace(/\s+/g, " ");

  return (
    <Document>
      <Page size={PDF_PAGE} style={S.root}>
        <View style={S.photoBox}>
          <Image src={photoSrc} style={S.personImage} />
        </View>

        <View style={S.contactBox} />
        <View style={S.contactTitle}><Text style={S.text}>¿Tienes información sobre su paradero?</Text></View>
        <View style={S.contactSubtitle}><Text style={S.text}>Comunícate con nosotros</Text></View>
        <View style={S.phoneText}><Text style={S.text}>{CONTACT_INFO.PHONE}</Text></View>
        <View style={S.emailText}><Text style={S.text}>{CONTACT_INFO.EMAIL}</Text></View>
        <View style={S.redesRow}>
          <RedesIcon d={FACEBOOK_PATH} />
          <RedesIcon d={X_PATH} />
          <Text style={S.redesText}>{CONTACT_INFO.SOCIAL_MEDIA}</Text>
        </View>
        <View style={S.qrBox}>
          <View style={S.qrCode}>
            <Image src={qrCodeString} style={S.qrImage} />
          </View>
        </View>

        <Image src={LOGO_COBUPEJ_CEDULA_PNG} style={S.logoCobupej} />
        <Image src={LOGO_JAL_CEDULA_PNG} style={S.logoJal} />

        <View style={[S.title, { color: titular.color }]}><Text style={S.text}>{titular.text}</Text></View>
        <View style={S.name}><Text style={S.text}>{hasInfoField(nombre_completo)}</Text></View>

        <View style={S.fechaBox}>
          <Text style={S.fechaLine}>
            <Text style={S.fechaLabel}>Se le vio por última vez el: </Text>
            <Text style={S.fechaValue}>{formatDate(fecha_desaparicion)}</Text>
          </Text>
          <Text style={S.fechaLine}>
            <Text style={S.lugarLabel}>En: </Text>
            <Text style={S.lugarValue}>{ubicacion}</Text>
          </Text>
        </View>

        <View style={S.edadBox}>
          <Text style={S.dataLabel}>Edad al momento de la desaparición:</Text>
          <Text style={S.dataValue}>{verifyAge(edad_momento_desaparicion)}</Text>
        </View>
        <View style={S.sexoGeneroBox}>
          <Row label="Sexo" value={hasInfoField(sexo)} />
          <Row label="Género" value={hasInfoField(genero)} />
        </View>

        <View style={S.fisicasBox}>
          <Text style={S.boxHeader}>Características físicas:</Text>
          <Row label="Estatura" value={verifyStature(estatura)} />
          <Row label="Complexión" value={hasInfoField(complexion)} />
          <Row label="Tez" value={hasInfoField(tez)} />
        </View>
        <View style={S.ojosBox}>
          <Row label="Color de ojos" value={hasInfoField(ojos_color)} />
          <Row label="Cabello" value={hasInfoField(cabello)} />
        </View>

        <View style={S.senasBox}>
          <Text style={S.boxHeader}>Señas particulares:</Text>
          <View style={S.boxGap} />
          <Text style={[S.boxText, boxLines(PNG_IMP_L.senasSpace)]}>{senasText}</Text>
        </View>
        <View style={S.vestimentaBox}>
          <Text style={S.boxHeader}>Vestimenta:</Text>
          <View style={S.boxGap} />
          <Text style={[S.boxText, boxLines(PNG_IMP_L.vestimentaSpace)]}>{vestimentaText}</Text>
        </View>

        <View style={S.aviso}><Text style={S.text}>{DISCLAIMER}</Text></View>
      </Page>
    </Document>
  );
};

export default MissingPersonPdf;
